import React, { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { Landmark, TrendingUp, Wallet, BarChart3, Plus, Save, X, Edit3, Trash2, Target, ArrowLeftRight } from "lucide-react";
import { useBankingStore, BankAccount, BankAccountInput } from "../../store/bankingStore";
import { useThemeStore } from "../../store/themeStore";
import BankLogo, { BANK_META } from "../BankLogo";
import Select from "../ui/Select";
import toast from "react-hot-toast";
import { pageContainerVariants, pageItemVariants } from "../../constants/motionVariants";
import { buttonClass } from '../ui/Button';

const MASK = "••••••";

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  savings: "ออมทรัพย์",
  fixed: "ฝากประจำ",
  current: "กระแสรายวัน",
};

const ACCOUNT_TYPE_COLOR: Record<string, string> = {
  savings: "bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300",
  fixed: "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300",
  current: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
};

const THAI_BANKS = [
  { key: 'kbank', name: 'ธนาคารกสิกรไทย',     icon: '💚', color: 'green'  },
  { key: 'scb',   name: 'ธนาคารไทยพาณิชย์',   icon: '💜', color: 'purple' },
  { key: 'ktb',   name: 'ธนาคารกรุงไทย',      icon: '💙', color: 'blue'   },
  { key: 'bbl',   name: 'ธนาคารกรุงเทพ',      icon: '🔵', color: 'blue'   },
  { key: 'bay',   name: 'ธนาคารกรุงศรีอยุธยา', icon: '❤️', color: 'red'    },
  { key: 'ttb',   name: 'ทหารไทยธนชาต',       icon: '🔷', color: 'cyan'   },
  { key: 'gsb',   name: 'ธนาคารออมสิน',       icon: '💜', color: 'purple' },
  { key: 'ghb',   name: 'อาคารสงเคราะห์',     icon: '🟠', color: 'orange' },
  { key: 'other', name: 'อื่นๆ',              icon: '🏦', color: 'gray'   },
];

type BankFormState = {
  bank_key: string; bank_name: string; bank_icon: string; bank_color: string
  account_name: string; account_number: string
  account_type: 'savings' | 'fixed' | 'current'
  balance: string; currency: string; interest_rate: string; notes: string
  savings_goal: string
}

const emptyBankForm: BankFormState = {
  bank_key: 'kbank', bank_name: 'ธนาคารกสิกรไทย', bank_icon: '💚', bank_color: 'green',
  account_name: '', account_number: '',
  account_type: 'savings',
  balance: '', currency: 'THB', interest_rate: '', notes: '',
  savings_goal: '',
};

function maskAccountNumber(num?: string) {
  if (!num) return "";
  const digits = num.replace(/\D/g, "");
  if (digits.length < 4) return num;
  return (
    digits.slice(0, 3) + "-" + digits.slice(3, 4) + "-" +
    digits.slice(4, -1).replace(/./g, "x") + "-" + digits.slice(-1)
  );
}

function formatTHB(n: number) {
  return "฿" + n.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function useCountUp(target: number, duration = 0.7) {
  const [value, setValue] = useState(target * 0.85)
  const prevRef = useRef(target * 0.85)

  useEffect(() => {
    const controls = animate(prevRef.current, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(v),
    })
    prevRef.current = target
    return () => controls.stop()
  }, [target, duration])

  return value
}

const AccountRow: React.FC<{
  account: BankAccount;
  index: number;
  onEdit: (account: BankAccount) => void;
  onDelete: (id: number) => void;
  deletingId: number | null;
  onConfirmDelete: (id: number) => void;
  onCancelDelete: () => void;
}> = ({ account, index, onEdit, onDelete, deletingId, onConfirmDelete, onCancelDelete }) => {
  const annualIncome = (account.balance * account.interest_rate) / 100;
  const bankColor = BANK_META[account.bank_name]?.bg ?? "#6b7280";
  const { maskAmounts } = useThemeStore();
  const m = (v: string) => (maskAmounts ? MASK : v);

  const goal = account.savings_goal ?? null;
  const goalPct = goal && goal > 0 ? Math.min((account.balance / goal) * 100, 100) : null;
  const goalReached = goalPct !== null && goalPct >= 100;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
    >
      {/* Left bank color accent */}
      <div
        className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full opacity-70 group-hover:opacity-100 transition-opacity"
        style={{ background: bankColor }}
      />

      {/* Logo */}
      <div className="shrink-0 rounded-xl ring-1 ring-gray-100 dark:ring-gray-700/60 shadow-sm overflow-hidden">
        <BankLogo bankName={account.bank_name} fallbackIcon={account.bank_icon} size={38} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-[13px] text-gray-900 dark:text-white truncate leading-tight">
            {account.account_name}
          </span>
          <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wide ${ACCOUNT_TYPE_COLOR[account.account_type] ?? "bg-gray-100 text-gray-600"}`}>
            {ACCOUNT_TYPE_LABEL[account.account_type] ?? account.account_type}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-400 leading-tight">
          <span className="truncate">{account.bank_name}</span>
          {account.account_number && (
            <>
              <span className="text-gray-200 dark:text-gray-400 shrink-0">·</span>
              <span className="font-mono shrink-0">{maskAccountNumber(account.account_number)}</span>
            </>
          )}
          {account.interest_rate > 0 && (
            <>
              <span className="text-gray-200 dark:text-gray-400 shrink-0">·</span>
              <span className="text-primary-500 dark:text-primary-400 font-semibold shrink-0">
                ↗ {account.interest_rate.toFixed(2)}%
              </span>
            </>
          )}
        </div>

        {/* Savings goal — slim inline bar */}
        {goal !== null && goalPct !== null && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden max-w-[120px]">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${goalPct}%` }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: index * 0.05 + 0.15 }}
                className={`h-full rounded-full ${goalReached ? "bg-gain-gradient" : "bg-gradient-to-r from-blue-500 to-purple-600"}`}
              />
            </div>
            <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-400 shrink-0">
              <span>{goalReached ? "🎉 ถึงเป้า" : `${goalPct.toFixed(0)}%`}</span>
              <span className="text-gray-200 dark:text-gray-400">·</span>
              <Target className="w-2.5 h-2.5" />
              <span>{m(formatTHB(goal))}</span>
            </div>
          </div>
        )}
      </div>

      {/* Balance */}
      <div className="shrink-0 text-right mr-1">
        <div className="font-bold text-[14px] text-gray-900 dark:text-white tabular-nums leading-tight">
          {m(formatTHB(account.balance))}
        </div>
        {annualIncome > 0 && (
          <div className="text-[11px] font-medium text-primary-600 dark:text-primary-400 mt-0.5 leading-tight">
            +{m(formatTHB(annualIncome))}/ปี
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(account)}
          className="p-1.5 text-gray-300 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        {deletingId === account.id ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onConfirmDelete(account.id)}
              className={buttonClass('danger', 'sm')}
            >ลบ</button>
            <button
              onClick={onCancelDelete}
              className={buttonClass('secondary', 'sm')}
            >ยกเลิก</button>
          </div>
        ) : (
          <button
            onClick={() => onDelete(account.id)}
            className="p-1.5 text-gray-300 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

const BankingPage: React.FC = () => {
  const { accounts, loading, loadAccounts, createAccount, updateAccount, deleteAccount, transferBetweenAccounts } = useBankingStore();
  const { maskAmounts } = useThemeStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [form, setForm] = useState<BankFormState>(emptyBankForm);

  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState<string>('');
  const [transferTo, setTransferTo] = useState<string>('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => { loadAccounts(); }, []);

  const totalBalance = accounts.reduce((s, a) => s + a.balance, 0);
  const totalIncome = accounts.reduce((s, a) => s + (a.balance * a.interest_rate) / 100, 0);
  const animatedBalance = useCountUp(totalBalance);
  const animatedIncome = useCountUp(totalIncome);
  const weightedRate = totalBalance > 0
    ? accounts.reduce((s, a) => s + (a.balance / totalBalance) * a.interest_rate, 0)
    : 0;

  const openTransfer = () => {
    const first = accounts[0]?.id.toString() ?? '';
    const second = accounts[1]?.id.toString() ?? '';
    setTransferFrom(first);
    setTransferTo(second !== first ? second : '');
    setTransferAmount('');
    setIsTransferOpen(true);
  };

  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount);
    if (!transferFrom || !transferTo) { toast.error('เลือกบัญชีต้นทางและปลายทาง'); return; }
    if (transferFrom === transferTo) { toast.error('ต้นทางและปลายทางต้องไม่ใช่บัญชีเดียวกัน'); return; }
    if (!amount || amount <= 0) { toast.error('ระบุจำนวนเงินที่ถูกต้อง'); return; }
    setTransferring(true);
    try {
      await transferBetweenAccounts(parseInt(transferFrom), parseInt(transferTo), amount);
      setIsTransferOpen(false);
    } catch {} finally {
      setTransferring(false);
    }
  };

  const openAdd = () => { setForm(emptyBankForm); setEditingId(null); setIsModalOpen(true); };
  const openEdit = (account: BankAccount) => {
    const bank = THAI_BANKS.find(b => b.name === account.bank_name) || THAI_BANKS[THAI_BANKS.length - 1];
    setForm({
      bank_key: bank.key, bank_name: account.bank_name,
      bank_icon: account.bank_icon, bank_color: account.bank_color,
      account_name: account.account_name,
      account_number: account.account_number || '',
      account_type: account.account_type,
      balance: String(account.balance),
      currency: account.currency,
      interest_rate: String(account.interest_rate),
      notes: account.notes || '',
      savings_goal: account.savings_goal ? String(account.savings_goal) : '',
    });
    setEditingId(account.id);
    setIsModalOpen(true);
  };
  const closeModal = () => { setIsModalOpen(false); setEditingId(null); setForm(emptyBankForm); };

  const handleSave = async () => {
    if (!form.bank_name || !form.account_name) {
      toast.error('กรุณากรอกธนาคารและชื่อบัญชี');
      return;
    }
    const payload: BankAccountInput = {
      bank_name: form.bank_name, bank_icon: form.bank_icon, bank_color: form.bank_color,
      account_name: form.account_name,
      account_number: form.account_number || undefined,
      account_type: form.account_type,
      balance: parseFloat(form.balance) || 0,
      currency: 'THB',
      interest_rate: parseFloat(form.interest_rate) || 0,
      notes: form.notes || undefined,
      savings_goal: form.savings_goal ? parseFloat(form.savings_goal) : null,
    };
    try {
      if (editingId) { await updateAccount(editingId, payload); }
      else { await createAccount(payload); }
      closeModal();
    } catch {}
  };

  return (
    <motion.div variants={pageContainerVariants} initial="hidden" animate="visible" className="space-y-4">
      {/* Hero + Account List Card */}
      <motion.div variants={pageItemVariants} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl border border-white/20 dark:border-gray-700/50 overflow-hidden shadow-sm">
        <div className="relative overflow-hidden px-6 py-8 bg-brand-soft">
          {/* Decorative glow orbs */}
          <div className="pointer-events-none absolute -top-20 -right-12 w-60 h-60 bg-white/50 dark:bg-primary-400/10 rounded-full blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-12 w-60 h-60 bg-primary-200/50 dark:bg-primary-400/10 rounded-full blur-3xl" />
          {/* Faint large landmark watermark */}
          <Landmark className="pointer-events-none absolute -right-4 -bottom-6 w-40 h-40 text-primary-600/[0.07] dark:text-white/[0.04]" strokeWidth={1} />

          <div className="relative flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-lg bg-white/70 dark:bg-white/10 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/60 dark:ring-white/15 shadow-sm">
                  <Wallet className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                </div>
                <p className="text-gray-500/80 dark:text-gray-400/80 text-[11px] font-semibold tracking-widest uppercase">
                  ยอดเงินรวมทั้งหมด
                </p>
              </div>
              <p className="text-4xl font-bold text-primary-900 dark:text-white tracking-tight tabular-nums">
                {maskAmounts ? MASK : formatTHB(animatedBalance)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {accounts.length >= 2 && (
                <button
                  onClick={openTransfer}
                  className={buttonClass('secondary', 'sm')}
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  โอนเงิน
                </button>
              )}
              <button
                onClick={openAdd}
                className={buttonClass('secondary', 'sm')}
              >
                <Plus className="w-3.5 h-3.5" />
                เพิ่มบัญชี
              </button>
            </div>
          </div>
          <div className="relative grid grid-cols-3 gap-3">
            <div className="bg-white/55 dark:bg-white/[0.06] ring-1 ring-white/50 dark:ring-white/10 rounded-2xl px-4 py-3 backdrop-blur-sm shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Wallet className="w-3.5 h-3.5 text-primary-500 dark:text-primary-300" />
                <span className="text-primary-600/80 dark:text-primary-200/70 text-[10px] font-semibold uppercase tracking-wider">บัญชี</span>
              </div>
              <p className="text-primary-900 dark:text-white text-xl font-bold leading-none">{accounts.length}</p>
              <p className="text-primary-500/70 dark:text-primary-200/50 text-[10px] mt-1.5">ที่ลงทะเบียน</p>
            </div>
            <div className="bg-white/55 dark:bg-white/[0.06] ring-1 ring-white/50 dark:ring-white/10 rounded-2xl px-4 py-3 backdrop-blur-sm shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-primary-500 dark:text-primary-300" />
                <span className="text-primary-600/80 dark:text-primary-200/70 text-[10px] font-semibold uppercase tracking-wider">ดอกเบี้ย</span>
              </div>
              <p className="text-primary-900 dark:text-white text-xl font-bold leading-none">{weightedRate.toFixed(3)}%</p>
              <p className="text-primary-500/70 dark:text-primary-200/50 text-[10px] mt-1.5">Weighted avg. p.a.</p>
            </div>
            <div className="bg-white/55 dark:bg-white/[0.06] ring-1 ring-white/50 dark:ring-white/10 rounded-2xl px-4 py-3 backdrop-blur-sm shadow-sm">
              <div className="flex items-center gap-1.5 mb-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-primary-500 dark:text-primary-300" />
                <span className="text-primary-600/80 dark:text-primary-200/70 text-[10px] font-semibold uppercase tracking-wider">รายได้/ปี</span>
              </div>
              <p className="text-primary-900 dark:text-white text-xl font-bold tabular-nums truncate leading-none">{maskAmounts ? MASK : formatTHB(animatedIncome)}</p>
              <p className="text-primary-500/70 dark:text-primary-200/50 text-[10px] mt-1.5">คำนวณจากยอดปัจจุบัน</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">กำลังโหลด...</div>
        ) : accounts.length === 0 ? (
          <div className="py-16 text-center">
            <Landmark className="w-10 h-10 text-gray-300 dark:text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">ยังไม่มีบัญชีธนาคาร</p>
            <p className="text-sm text-gray-400 dark:text-gray-400 mb-4">กดปุ่มเพิ่มบัญชีเพื่อเริ่มต้น</p>
            <button
              onClick={openAdd}
              className={buttonClass('primary', 'md')}
            >
              <Plus className="w-4 h-4" />
              เพิ่มบัญชีธนาคาร
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/40 py-1">
            {accounts.map((account, i) => (
              <AccountRow
                key={account.id}
                account={account}
                index={i}
                onEdit={openEdit}
                onDelete={(id) => setDeletingId(id)}
                deletingId={deletingId}
                onConfirmDelete={async (id) => { await deleteAccount(id); setDeletingId(null); }}
                onCancelDelete={() => setDeletingId(null)}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Transfer Modal */}
      {isTransferOpen && (
        <div className="sheet fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white">โอนเงินระหว่างบัญชี</h4>
              </div>
              <button
                onClick={() => setIsTransferOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">บัญชีต้นทาง</label>
                <Select
                  value={transferFrom}
                  onChange={setTransferFrom}
                  options={accounts
                    .filter(a => a.id.toString() !== transferTo)
                    .map(a => ({
                      value: a.id.toString(),
                      label: `${a.account_name} — ${formatTHB(a.balance)}`,
                      iconNode: <BankLogo bankName={a.bank_name} fallbackIcon={a.bank_icon} size={18} />,
                    }))}
                />
              </div>

              <div className="flex justify-center">
                <div className="w-8 h-8 rounded-full bg-brand-soft flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 text-primary-500 rotate-90" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">บัญชีปลายทาง</label>
                <Select
                  value={transferTo}
                  onChange={setTransferTo}
                  options={accounts
                    .filter(a => a.id.toString() !== transferFrom)
                    .map(a => ({
                      value: a.id.toString(),
                      label: `${a.account_name} — ${formatTHB(a.balance)}`,
                      iconNode: <BankLogo bankName={a.bank_name} fallbackIcon={a.bank_icon} size={18} />,
                    }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">จำนวนเงิน (฿)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={transferAmount}
                  onChange={e => setTransferAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
                {transferFrom && (() => {
                  const src = accounts.find(a => a.id.toString() === transferFrom);
                  return src ? (
                    <p className="text-[11px] text-gray-400 mt-1">ยอดคงเหลือ: <span className="font-semibold text-gray-600 dark:text-gray-300">{formatTHB(src.balance)}</span></p>
                  ) : null;
                })()}
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={() => setIsTransferOpen(false)}
                className={buttonClass('secondary', 'md')}
              >
                <X className="w-4 h-4" />
                ยกเลิก
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring}
                className={buttonClass('primary', 'md')}
              >
                <ArrowLeftRight className="w-4 h-4" />
                {transferring ? 'กำลังโอน...' : 'โอนเงิน'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Account Modal */}
      {isModalOpen && (
        <div className="sheet fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                  <Landmark className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {editingId ? 'แก้ไขบัญชี' : 'เพิ่มบัญชีธนาคาร'}
                </h4>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ธนาคาร *</label>
                  <Select
                    value={form.bank_key}
                    onChange={key => {
                      const bank = THAI_BANKS.find(b => b.key === key) || THAI_BANKS[THAI_BANKS.length - 1];
                      setForm(prev => ({ ...prev, bank_key: bank.key, bank_name: bank.name, bank_icon: bank.icon, bank_color: bank.color }));
                    }}
                    options={THAI_BANKS.map(b => ({ value: b.key, label: b.name, iconNode: <BankLogo bankName={b.name} size={18} /> }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ประเภทบัญชี</label>
                  <Select
                    value={form.account_type}
                    onChange={v => setForm(prev => ({ ...prev, account_type: v as 'savings' | 'fixed' | 'current' }))}
                    options={[
                      { value: 'savings', label: 'ออมทรัพย์' },
                      { value: 'fixed', label: 'ฝากประจำ' },
                      { value: 'current', label: 'กระแสรายวัน' },
                    ]}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ชื่อบัญชี *</label>
                  <input type="text" value={form.account_name}
                    onChange={e => setForm(prev => ({ ...prev, account_name: e.target.value }))}
                    placeholder="เช่น บัญชีเงินเดือน"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">เลขบัญชี</label>
                  <input type="text" value={form.account_number}
                    onChange={e => setForm(prev => ({ ...prev, account_number: e.target.value }))}
                    placeholder="xxx-x-xxxxx-x"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ยอดเงิน (฿)</label>
                  <input type="number" step="any" value={form.balance}
                    onChange={e => setForm(prev => ({ ...prev, balance: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ดอกเบี้ย (% ต่อปี)</label>
                  <input type="number" step="0.001" value={form.interest_rate}
                    onChange={e => setForm(prev => ({ ...prev, interest_rate: e.target.value }))}
                    placeholder="1.500"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">เป้าหมายการออม (฿)</label>
                  <input type="number" step="any" value={form.savings_goal}
                    onChange={e => setForm(prev => ({ ...prev, savings_goal: e.target.value }))}
                    placeholder="เช่น 1000000"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">หมายเหตุ</label>
                  <input type="text" value={form.notes}
                    onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="(ไม่บังคับ)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={closeModal}
                className={buttonClass('secondary', 'md')}
              >
                <X className="w-4 h-4" />
                ยกเลิก
              </button>
              <button
                onClick={handleSave}
                className={buttonClass('primary', 'md')}
              >
                <Save className="w-4 h-4" />
                {editingId ? 'บันทึกการแก้ไข' : 'เพิ่มบัญชี'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default BankingPage;
